import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Settings, RefreshCw, Plus, Trash2, Info, Download, Upload, 
  Users, ClipboardList, Scale, ChevronRight, AlertTriangle, Check, X,
  FileText, GripVertical, ArrowUp, ArrowDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// API client with Basic Auth
const createApiClient = (credentials) => {
  const instance = axios.create({
    baseURL: API,
    headers: {
      "Content-Type": "application/json",
    },
    auth: credentials ? { username: credentials.username, password: credentials.password } : undefined,
  });
  return instance;
};

// Login Page Component
const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const client = createApiClient({ username, password });
      await client.get("/auth/check");
      onLogin({ username, password });
    } catch (err) {
      setError("Неверные учетные данные");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-page">
      <div className="login-card animate-fade-in">
        <h1 className="login-title">Утилита ПЗ</h1>
        <p className="login-subtitle">Войдите для создания производственных заданий</p>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <Label htmlFor="username" className="text-sm font-medium mb-2 block">Логин</Label>
            <Input
              id="username"
              data-testid="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              className="w-full"
              autoComplete="username"
            />
          </div>
          
          <div className="mb-6">
            <Label htmlFor="password" className="text-sm font-medium mb-2 block">Пароль</Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full"
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm mb-4" data-testid="login-error">{error}</div>
          )}
          
          <Button 
            type="submit" 
            data-testid="login-submit"
            className="w-full bg-blue-600 hover:bg-blue-700" 
            disabled={loading}
          >
            {loading ? <span className="loading-spinner"></span> : "Войти"}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Main App Component
const MainApp = ({ credentials, onLogout }) => {
  const api = createApiClient(credentials);
  
  // State
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [processingPlans, setProcessingPlans] = useState([]);
  const [stores, setStores] = useState([]);
  const [states, setStates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [productionTasks, setProductionTasks] = useState([]);
  const [stock, setStock] = useState({});
  
  // Employee schedule state (hours per day)
  const [employeeSchedule, setEmployeeSchedule] = useState({});
  
  // Production task creation state
  const [taskName, setTaskName] = useState("");
  const [selectedPlans, setSelectedPlans] = useState([]); // [{planId, volume, details}]
  const [settings, setSettings] = useState({
    materialsStoreId: "",
    productsStoreId: "",
    stateId: "",
    organizationId: "",
  });
  
  // Balancing state - now tracks individual stage instances
  const [balancing, setBalancing] = useState({
    assignments: {}, // {employeeId: {name, totalPlanned, allocated, stageInstances: [{planIndex, stageIndex, stageName, hours}]}}
    unassignedStages: [], // [{planIndex, stageIndex, stageName, hours, suggestedEmployees}]
    totalUnderload: 0,
    totalOverload: 0,
  });

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [planSelectorOpen, setPlanSelectorOpen] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  // Drag and drop state
  const [draggedStage, setDraggedStage] = useState(null);

  const weekDays = [
    { key: "mon", label: "Пн" },
    { key: "tue", label: "Вт" },
    { key: "wed", label: "Ср" },
    { key: "thu", label: "Чт" },
    { key: "fri", label: "Пт" },
    { key: "sat", label: "Сб" },
    { key: "sun", label: "Вс" },
  ];

  // Load initial data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get("/employees"),
        api.get("/processingplans"),
        api.get("/stores"),
        api.get("/states"),
        api.get("/organizations"),
        api.get("/productiontasks"),
      ]);

      // Process results even if some failed
      const [empRes, plansRes, storesRes, statesRes, orgsRes, tasksRes] = results;
      
      if (empRes.status === 'fulfilled') setEmployees(empRes.value.data);
      if (plansRes.status === 'fulfilled') setProcessingPlans(plansRes.value.data);
      if (storesRes.status === 'fulfilled') setStores(storesRes.value.data);
      if (statesRes.status === 'fulfilled') setStates(statesRes.value.data);
      if (orgsRes.status === 'fulfilled') setOrganizations(orgsRes.value.data);
      if (tasksRes.status === 'fulfilled') setProductionTasks(tasksRes.value.data);

      // Set defaults from successfully loaded data
      const loadedStores = storesRes.status === 'fulfilled' ? storesRes.value.data : [];
      const loadedStates = statesRes.status === 'fulfilled' ? statesRes.value.data : [];
      const loadedOrgs = orgsRes.status === 'fulfilled' ? orgsRes.value.data : [];
      const loadedEmployees = empRes.status === 'fulfilled' ? empRes.value.data : [];
      
      if (loadedStores.length > 0) {
        const defaultStore = loadedStores.find(s => s.name.includes("Основной")) || loadedStores[0];
        setSettings(prev => ({
          ...prev,
          materialsStoreId: defaultStore.id,
          productsStoreId: defaultStore.id,
        }));
        
        // Load stock for default store (don't wait)
        api.get(`/stock/${defaultStore.id}`).then(res => setStock(res.data)).catch(() => {});
      }
      
      if (loadedStates.length > 0) {
        const defaultState = loadedStates.find(s => s.name.includes("Производство МП")) || loadedStates[0];
        setSettings(prev => ({ ...prev, stateId: defaultState.id }));
      }
      
      if (loadedOrgs.length > 0) {
        setSettings(prev => ({ ...prev, organizationId: loadedOrgs[0].id }));
      }

      // Initialize employee schedule from localStorage
      const savedSchedule = localStorage.getItem("employeeSchedule");
      if (savedSchedule) {
        setEmployeeSchedule(JSON.parse(savedSchedule));
      } else if (loadedEmployees.length > 0) {
        const initialSchedule = {};
        loadedEmployees.forEach(emp => {
          initialSchedule[emp.id] = { mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0, sun: 0 };
        });
        setEmployeeSchedule(initialSchedule);
      }

      // Check if any requests failed
      const failedCount = results.filter(r => r.status === 'rejected').length;
      if (failedCount > 0) {
        toast.warning(`Загружено частично (${6 - failedCount}/6)`);
      } else {
        toast.success("Данные загружены");
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      toast.error("Ошибка загрузки данных. Попробуйте обновить.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  // Save schedule to localStorage when it changes
  useEffect(() => {
    if (Object.keys(employeeSchedule).length > 0) {
      localStorage.setItem("employeeSchedule", JSON.stringify(employeeSchedule));
    }
  }, [employeeSchedule]);

  // Refresh reference data
  const handleRefresh = async () => {
    setLoading(true);
    try {
      await api.get("/reference/refresh");
      await loadData();
      toast.success("Данные обновлены");
    } catch (err) {
      toast.error("Ошибка обновления");
    } finally {
      setLoading(false);
    }
  };

  // Update employee hours
  const updateEmployeeHours = (employeeId, day, value) => {
    const numValue = parseFloat(value) || 0;
    setEmployeeSchedule(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [day]: Math.max(0, Math.min(24, numValue)),
      },
    }));
  };

  // Calculate total hours for employee
  const getTotalHours = (employeeId) => {
    const schedule = employeeSchedule[employeeId] || {};
    return Object.values(schedule).reduce((sum, h) => sum + (h || 0), 0);
  };

  // Add processing plan to task
  const addPlanToTask = async (planId) => {
    const plan = processingPlans.find(p => p.id === planId);
    setPlanSelectorOpen(false);
    
    try {
      toast.info(`Загрузка техкарты ${plan?.name || ''}...`);
      const detailsRes = await api.get(`/processingplans/${planId}/details`, { timeout: 45000 });
      
      setSelectedPlans(prev => [...prev, {
        planId,
        planName: plan?.name || "",
        pathName: plan?.pathName || "",
        volume: 1,
        details: detailsRes.data,
      }]);
      
      toast.success(`Добавлена техкарта: ${plan?.name}`);
    } catch (err) {
      console.error("Failed to load plan details:", err);
      // Add plan with empty details - can be refreshed later
      setSelectedPlans(prev => [...prev, {
        planId,
        planName: plan?.name || "",
        pathName: plan?.pathName || "",
        volume: 1,
        details: { stages: [], materials: [], products: [], totalHours: 0 },
      }]);
      toast.warning(`Техкарта добавлена без деталей. Нажмите "Обновить" для загрузки.`);
    }
  };

  // Update plan volume (step = 1)
  const updatePlanVolume = (index, delta) => {
    setSelectedPlans(prev => prev.map((p, i) => 
      i === index ? { ...p, volume: Math.max(1, Math.round(p.volume + delta)) } : p
    ));
  };

  // Set plan volume directly
  const setPlanVolume = (index, value) => {
    const numValue = Math.max(1, Math.round(parseFloat(value) || 1));
    setSelectedPlans(prev => prev.map((p, i) => 
      i === index ? { ...p, volume: numValue } : p
    ));
  };

  // Remove plan from task
  const removePlanFromTask = (index) => {
    setSelectedPlans(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate aggregated stages with individual instances for balancing
  const getStageInstances = useCallback(() => {
    const instances = [];
    
    selectedPlans.forEach((plan, planIndex) => {
      const stages = plan.details?.stages || [];
      const volume = Math.round(plan.volume);
      
      // Create instance for each unit of volume
      for (let unitIndex = 0; unitIndex < volume; unitIndex++) {
        stages.forEach((stage, stageIndex) => {
          instances.push({
            id: `${planIndex}-${unitIndex}-${stageIndex}`,
            planIndex,
            planName: plan.planName,
            unitIndex,
            stageIndex,
            stage_id: stage.stage_id,
            stageName: stage.name,
            hours: stage.standardHour || 0,
            performers: stage.performers || [],
            allPerformers: stage.all_performers || false,
          });
        });
      }
    });
    
    return instances;
  }, [selectedPlans]);

  // Calculate aggregated stages for display
  const getAggregatedStages = useCallback(() => {
    const stageMap = {};
    
    selectedPlans.forEach(plan => {
      const stages = plan.details?.stages || [];
      stages.forEach(stage => {
        const key = stage.stage_id || stage.name;
        const hours = (stage.standardHour || 0) * plan.volume;
        
        if (!stageMap[key]) {
          stageMap[key] = {
            id: stage.stage_id,
            name: stage.name,
            totalHours: 0,
            performers: stage.performers || [],
            allPerformers: stage.all_performers || false,
          };
        }
        stageMap[key].totalHours += hours;
      });
    });
    
    return Object.values(stageMap);
  }, [selectedPlans]);

  // Calculate aggregated materials
  const getAggregatedMaterials = useCallback(() => {
    const materialMap = {};
    
    selectedPlans.forEach(plan => {
      const materials = plan.details?.materials || [];
      materials.forEach(mat => {
        const key = mat.product_id;
        const qty = (mat.quantity || 0) * plan.volume;
        
        if (!materialMap[key]) {
          materialMap[key] = {
            id: mat.product_id,
            name: mat.name,
            totalQuantity: 0,
          };
        }
        materialMap[key].totalQuantity += qty;
      });
    });
    
    return Object.values(materialMap);
  }, [selectedPlans]);

  // Calculate total output products
  const getTotalOutputProducts = useCallback(() => {
    const productMap = {};
    
    selectedPlans.forEach(plan => {
      const products = plan.details?.products || [];
      products.forEach(prod => {
        const key = prod.product_id || prod.name;
        const qty = (prod.quantity || 0) * plan.volume;
        
        if (!productMap[key]) {
          productMap[key] = {
            id: prod.product_id,
            name: prod.name,
            totalQuantity: 0,
          };
        }
        productMap[key].totalQuantity += qty;
      });
    });
    
    return Object.values(productMap);
  }, [selectedPlans]);

  // Check if stage has available workers in schedule
  const hasAvailableWorkers = useCallback((stage) => {
    if (stage.allPerformers) return true;
    
    const performerIds = stage.performers?.map(p => p.id) || [];
    return performerIds.some(id => getTotalHours(id) > 0);
  }, [employeeSchedule]);

  // Check if employee can perform stage
  const canEmployeePerformStage = useCallback((employeeId, stageInstance) => {
    if (stageInstance.allPerformers) return true;
    const performerIds = stageInstance.performers?.map(p => p.id) || [];
    return performerIds.includes(employeeId);
  }, []);

  // Auto-balance stages to employees (one stage instance = one employee)
  const autoBalance = useCallback(() => {
    const stageInstances = getStageInstances();
    const assignments = {};
    const unassigned = [];
    
    // Get employees with scheduled hours
    const availableEmployees = employees.filter(emp => getTotalHours(emp.id) > 0);
    
    // Initialize assignments
    availableEmployees.forEach(emp => {
      assignments[emp.id] = {
        name: emp.name,
        totalPlanned: getTotalHours(emp.id),
        allocated: 0,
        stageInstances: [],
      };
    });
    
    // Sort instances by hours (larger first)
    const sortedInstances = [...stageInstances].sort((a, b) => b.hours - a.hours);
    
    sortedInstances.forEach(instance => {
      // Find eligible employees
      let eligibleEmployees = [];
      
      if (instance.allPerformers) {
        eligibleEmployees = availableEmployees;
      } else {
        const performerIds = new Set(instance.performers?.map(p => p.id) || []);
        eligibleEmployees = availableEmployees.filter(emp => performerIds.has(emp.id));
      }
      
      if (eligibleEmployees.length === 0) {
        // No available workers
        const canPerform = employees.filter(emp => {
          if (instance.allPerformers) return true;
          const performerIds = new Set(instance.performers?.map(p => p.id) || []);
          return performerIds.has(emp.id);
        });
        
        unassigned.push({
          ...instance,
          suggestedEmployees: canPerform.map(e => e.name),
        });
        return;
      }
      
      // Find employee with most remaining capacity
      let bestEmployee = null;
      let maxRemaining = -Infinity;
      
      eligibleEmployees.forEach(emp => {
        const assignment = assignments[emp.id];
        const remaining = assignment.totalPlanned - assignment.allocated;
        if (remaining > maxRemaining) {
          maxRemaining = remaining;
          bestEmployee = emp;
        }
      });
      
      if (bestEmployee) {
        assignments[bestEmployee.id].stageInstances.push({
          id: instance.id,
          planIndex: instance.planIndex,
          planName: instance.planName,
          unitIndex: instance.unitIndex,
          stageName: instance.stageName,
          hours: instance.hours,
        });
        assignments[bestEmployee.id].allocated += instance.hours;
      }
    });
    
    // Calculate underload/overload
    let totalUnderload = 0;
    let totalOverload = 0;
    
    Object.values(assignments).forEach(a => {
      const diff = a.totalPlanned - a.allocated;
      if (diff > 0) totalUnderload += diff;
      else if (diff < 0) totalOverload += Math.abs(diff);
    });
    
    setBalancing({
      assignments,
      unassignedStages: unassigned,
      totalUnderload,
      totalOverload,
    });
  }, [employees, employeeSchedule, getStageInstances]);

  // Manual reassign stage (drag and drop)
  const reassignStage = useCallback((stageInstanceId, fromEmployeeId, toEmployeeId) => {
    if (fromEmployeeId === toEmployeeId) return;
    
    setBalancing(prev => {
      const newAssignments = { ...prev.assignments };
      
      // Find and remove from source
      let movedStage = null;
      if (fromEmployeeId && newAssignments[fromEmployeeId]) {
        const fromEmp = { ...newAssignments[fromEmployeeId] };
        const stageIdx = fromEmp.stageInstances.findIndex(s => s.id === stageInstanceId);
        if (stageIdx >= 0) {
          movedStage = fromEmp.stageInstances[stageIdx];
          fromEmp.stageInstances = fromEmp.stageInstances.filter((_, i) => i !== stageIdx);
          fromEmp.allocated -= movedStage.hours;
          newAssignments[fromEmployeeId] = fromEmp;
        }
      } else {
        // Moving from unassigned
        const unassignedIdx = prev.unassignedStages.findIndex(s => s.id === stageInstanceId);
        if (unassignedIdx >= 0) {
          const stage = prev.unassignedStages[unassignedIdx];
          movedStage = {
            id: stage.id,
            planIndex: stage.planIndex,
            planName: stage.planName,
            unitIndex: stage.unitIndex,
            stageName: stage.stageName,
            hours: stage.hours,
          };
        }
      }
      
      if (!movedStage) return prev;
      
      // Add to target
      if (toEmployeeId && newAssignments[toEmployeeId]) {
        const toEmp = { ...newAssignments[toEmployeeId] };
        toEmp.stageInstances = [...toEmp.stageInstances, movedStage];
        toEmp.allocated += movedStage.hours;
        newAssignments[toEmployeeId] = toEmp;
      }
      
      // Update unassigned list
      let newUnassigned = prev.unassignedStages;
      if (!fromEmployeeId) {
        newUnassigned = prev.unassignedStages.filter(s => s.id !== stageInstanceId);
      }
      if (!toEmployeeId) {
        // Find stage info from stageInstances
        const stageInstances = getStageInstances();
        const stageInfo = stageInstances.find(s => s.id === stageInstanceId);
        if (stageInfo) {
          const canPerform = employees.filter(emp => {
            if (stageInfo.allPerformers) return true;
            const performerIds = new Set(stageInfo.performers?.map(p => p.id) || []);
            return performerIds.has(emp.id);
          });
          newUnassigned = [...newUnassigned, { ...stageInfo, suggestedEmployees: canPerform.map(e => e.name) }];
        }
      }
      
      // Recalculate underload/overload
      let totalUnderload = 0;
      let totalOverload = 0;
      Object.values(newAssignments).forEach(a => {
        const diff = a.totalPlanned - a.allocated;
        if (diff > 0) totalUnderload += diff;
        else if (diff < 0) totalOverload += Math.abs(diff);
      });
      
      return {
        assignments: newAssignments,
        unassignedStages: newUnassigned,
        totalUnderload,
        totalOverload,
      };
    });
    
    toast.success("Этап перемещен");
  }, [employees, getStageInstances]);

  // Drag handlers
  const handleDragStart = (e, stageInstance, fromEmployeeId) => {
    setDraggedStage({ stageInstance, fromEmployeeId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, toEmployeeId) => {
    e.preventDefault();
    if (draggedStage) {
      // Check if employee can perform this stage
      const stageInstances = getStageInstances();
      const stageInfo = stageInstances.find(s => s.id === draggedStage.stageInstance.id);
      
      if (toEmployeeId && stageInfo && !canEmployeePerformStage(toEmployeeId, stageInfo)) {
        toast.error("Сотрудник не может выполнять этот этап");
        return;
      }
      
      reassignStage(draggedStage.stageInstance.id, draggedStage.fromEmployeeId, toEmployeeId);
    }
    setDraggedStage(null);
  };

  // Effect to auto-balance when plans change
  useEffect(() => {
    if (selectedPlans.length > 0) {
      autoBalance();
    } else {
      setBalancing({ assignments: {}, unassignedStages: [], totalUnderload: 0, totalOverload: 0 });
    }
  }, [selectedPlans, employeeSchedule, autoBalance]);

  // Create production task
  const createTask = async () => {
    if (!taskName.trim()) {
      toast.error("Введите название ПЗ");
      return;
    }
    if (selectedPlans.length === 0) {
      toast.error("Добавьте хотя бы одну техкарту");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: taskName,
        production_rows: selectedPlans.map(p => ({
          processingplan_id: p.planId,
          production_volume: p.volume,
        })),
        materials_store_id: settings.materialsStoreId,
        products_store_id: settings.productsStoreId,
        state_id: settings.stateId,
        organization_id: settings.organizationId,
      };

      const res = await api.post("/productiontasks", payload);
      toast.success(`ПЗ "${res.data.name}" создано`);
      
      // Reset form
      setTaskName("");
      setSelectedPlans([]);
      setBalancing({ assignments: {}, unassignedStages: [], totalUnderload: 0, totalOverload: 0 });
      
      // Refresh tasks list
      const tasksRes = await api.get("/productiontasks");
      setProductionTasks(tasksRes.data);
    } catch (err) {
      console.error("Failed to create task:", err);
      toast.error(err.response?.data?.detail || "Ошибка создания ПЗ");
    } finally {
      setLoading(false);
    }
  };

  // Export (load) existing task
  const loadExistingTask = async (taskId) => {
    setLoading(true);
    setExportDialogOpen(false);
    
    try {
      const res = await api.get(`/productiontasks/${taskId}`, { timeout: 30000 });
      const task = res.data;
      
      setTaskName(task.name);
      
      // Load details for each production row with error handling
      const planPromises = task.production_rows.map(async row => {
        try {
          const detailsRes = await api.get(`/processingplans/${row.processingplan_id}/details`, { timeout: 45000 });
          return {
            planId: row.processingplan_id,
            planName: row.processingplan_name,
            pathName: row.processingplan_pathName,
            volume: row.production_volume,
            details: detailsRes.data,
          };
        } catch (err) {
          console.warn(`Failed to load details for ${row.processingplan_name}`);
          return {
            planId: row.processingplan_id,
            planName: row.processingplan_name,
            pathName: row.processingplan_pathName,
            volume: row.production_volume,
            details: { stages: [], materials: [], products: [], totalHours: 0 },
          };
        }
      });
      
      const plansWithDetails = await Promise.all(planPromises);
      setSelectedPlans(plansWithDetails);
      
      setSettings(prev => ({
        ...prev,
        materialsStoreId: task.materials_store_id || prev.materialsStoreId,
        productsStoreId: task.products_store_id || prev.productsStoreId,
        stateId: task.state_id || prev.stateId,
        organizationId: task.organization_id || prev.organizationId,
      }));
      
      toast.success(`ПЗ "${task.name}" загружено`);
    } catch (err) {
      toast.error("Ошибка загрузки ПЗ");
    } finally {
      setLoading(false);
    }
  };

  // Generate PDF content
  const generatePDF = () => {
    const totalOutput = getTotalOutputProducts();
    const aggregatedStages = getAggregatedStages();
    const totalHours = aggregatedStages.reduce((sum, s) => sum + s.totalHours, 0);
    
    // Create printable content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>ПЗ: ${taskName || 'Без названия'}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
          h1 { color: #2563EB; border-bottom: 2px solid #2563EB; padding-bottom: 10px; }
          h2 { color: #1E40AF; margin-top: 30px; }
          h3 { color: #3B82F6; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #F1F5F9; font-weight: 600; }
          .summary-box { background: #EFF6FF; border: 1px solid #BFDBFE; border-radius: 8px; padding: 15px; margin: 15px 0; }
          .hours { font-weight: bold; color: #2563EB; }
          .employee-field { border-bottom: 1px solid #999; min-width: 150px; display: inline-block; }
          .total-row { background: #F8FAFC; font-weight: bold; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Производственное задание: ${taskName || 'Без названия'}</h1>
        
        <div class="summary-box">
          <strong>Общее время:</strong> <span class="hours">${totalHours.toFixed(2)} ч</span><br>
          <strong>Выходная продукция:</strong> ${totalOutput.map(p => `${p.name} × ${p.totalQuantity.toFixed(1)}`).join(', ') || 'Не указана'}
        </div>
        
        <h2>Содержимое ПЗ</h2>
        ${selectedPlans.map((plan, idx) => `
          <h3>${idx + 1}. ${plan.planName} (${plan.pathName}) × ${plan.volume} шт</h3>
          <table>
            <thead>
              <tr>
                <th>№</th>
                <th>Этап</th>
                <th>Норма-часы</th>
                <th>Исполнитель</th>
              </tr>
            </thead>
            <tbody>
              ${(plan.details?.stages || []).map((stage, sIdx) => `
                <tr>
                  <td>${sIdx + 1}</td>
                  <td>${stage.name}</td>
                  <td class="hours">${stage.standardHour?.toFixed(2) || 0} ч</td>
                  <td><span class="employee-field"></span></td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2">Итого на единицу</td>
                <td class="hours">${(plan.details?.stages || []).reduce((s, st) => s + (st.standardHour || 0), 0).toFixed(2)} ч</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        `).join('')}
        
        <h2>Балансировка по сотрудникам</h2>
        <table>
          <thead>
            <tr>
              <th>Сотрудник</th>
              <th>Плановые часы</th>
              <th>Загружено</th>
              <th>Этапы</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(balancing.assignments).map(([empId, data]) => `
              <tr>
                <td><strong>${data.name}</strong></td>
                <td>${data.totalPlanned.toFixed(1)} ч</td>
                <td class="hours">${data.allocated.toFixed(2)} ч</td>
                <td>${data.stageInstances.map(s => `${s.stageName} (${s.hours.toFixed(2)}ч)`).join(', ') || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        ${balancing.unassignedStages.length > 0 ? `
          <h3 style="color: #DC2626;">Нераспределенные этапы</h3>
          <ul>
            ${balancing.unassignedStages.map(s => `<li>${s.stageName} - ${s.hours.toFixed(2)}ч (могут: ${s.suggestedEmployees?.join(', ') || 'никто'})</li>`).join('')}
          </ul>
        ` : '<p style="color: #10B981;">✓ Все этапы распределены</p>'}
        
        <div style="margin-top: 40px; border-top: 1px solid #ddd; padding-top: 20px;">
          <p>Дата создания: ${new Date().toLocaleDateString('ru-RU')}</p>
        </div>
      </body>
      </html>
    `;
    
    // Open print dialog
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  // Group processing plans by folder
  const groupedPlans = processingPlans.reduce((acc, plan) => {
    const folder = plan.pathName || "Без папки";
    if (!acc[folder]) acc[folder] = [];
    acc[folder].push(plan);
    return acc;
  }, {});

  // Calculate totals
  const totalRequiredHours = getAggregatedStages().reduce((sum, s) => sum + s.totalHours, 0);
  const totalAvailableHours = employees.reduce((sum, emp) => sum + getTotalHours(emp.id), 0);
  const totalOutputProducts = getTotalOutputProducts();

  if (loading && employees.length === 0) {
    return (
      <div className="loading-overlay" data-testid="loading-overlay">
        <div className="flex flex-col items-center gap-4">
          <div className="loading-spinner w-8 h-8"></div>
          <span className="text-gray-500">Загрузка данных...</span>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="main-container" data-testid="main-app">
        {/* Header */}
        <header className="main-header">
          <div>
            <h1 className="main-title">Утилита создания ПЗ</h1>
            <p className="text-sm text-gray-500 mt-1">МойСклад • Планирование производства</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Обновить
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onLogout}
              data-testid="logout-btn"
            >
              Выйти
            </Button>
          </div>
        </header>

        {/* Section 1: Employee Schedule */}
        <section className="section-container" data-testid="schedule-section">
          <div className="section-header">
            <h2 className="section-title">
              <Users className="w-5 h-5 text-blue-600" />
              Часть 1. План выхода сотрудников
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-blue-600 tabular-nums">
                Доступно: {totalAvailableHours.toFixed(1)}ч
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="schedule-table">
              <thead>
                <tr>
                  <th className="min-w-[200px]">Сотрудник</th>
                  {weekDays.map(day => (
                    <th key={day.key} className="text-center w-20">{day.label}</th>
                  ))}
                  <th className="text-center w-24">Итого</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(emp => (
                  <tr key={emp.id} data-testid={`employee-row-${emp.id}`}>
                    <td>
                      <div className="employee-cell">
                        <span className="employee-name">{emp.name}</span>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="employee-info-icon" />
                          </TooltipTrigger>
                          <TooltipContent className="tooltip-content bg-slate-900">
                            <p className="font-semibold mb-2">Доступные этапы:</p>
                            {emp.available_stages?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {emp.available_stages.map(stage => (
                                  <span key={stage.id} className="tooltip-stage">{stage.name}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400">Нет назначенных этапов</span>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </td>
                    {weekDays.map(day => (
                      <td key={day.key} className="text-center">
                        <Input
                          type="number"
                          min="0"
                          max="24"
                          step="0.5"
                          className="hours-input"
                          value={employeeSchedule[emp.id]?.[day.key] || ""}
                          onChange={(e) => updateEmployeeHours(emp.id, day.key, e.target.value)}
                          placeholder="0"
                          data-testid={`hours-${emp.id}-${day.key}`}
                        />
                      </td>
                    ))}
                    <td className="text-center">
                      <span className="total-hours tabular-nums">{getTotalHours(emp.id).toFixed(1)}ч</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 2: Create Production Task */}
        <section className="section-container" data-testid="task-section">
          <div className="section-header">
            <h2 className="section-title">
              <ClipboardList className="w-5 h-5 text-blue-600" />
              Часть 2. Создание производственного задания
            </h2>
            <div className="flex items-center gap-2">
              {/* Settings Dialog */}
              <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="settings-btn">
                    <Settings className="w-4 h-4 mr-2" />
                    Настройки
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-white">
                  <DialogHeader>
                    <DialogTitle>Настройки ПЗ</DialogTitle>
                    <DialogDescription>Выберите параметры для создания производственного задания</DialogDescription>
                  </DialogHeader>
                  <div className="settings-grid">
                    <div className="settings-row">
                      <Label className="settings-label">Статус ПЗ</Label>
                      <Select value={settings.stateId} onValueChange={(v) => setSettings(p => ({...p, stateId: v}))}>
                        <SelectTrigger data-testid="state-select">
                          <SelectValue placeholder="Выберите статус" />
                        </SelectTrigger>
                        <SelectContent>
                          {states.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="settings-row">
                      <Label className="settings-label">Склад материалов</Label>
                      <Select value={settings.materialsStoreId} onValueChange={(v) => setSettings(p => ({...p, materialsStoreId: v}))}>
                        <SelectTrigger data-testid="materials-store-select">
                          <SelectValue placeholder="Выберите склад" />
                        </SelectTrigger>
                        <SelectContent>
                          {stores.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="settings-row">
                      <Label className="settings-label">Склад продукции</Label>
                      <Select value={settings.productsStoreId} onValueChange={(v) => setSettings(p => ({...p, productsStoreId: v}))}>
                        <SelectTrigger data-testid="products-store-select">
                          <SelectValue placeholder="Выберите склад" />
                        </SelectTrigger>
                        <SelectContent>
                          {stores.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="settings-row">
                      <Label className="settings-label">Организация</Label>
                      <Select value={settings.organizationId} onValueChange={(v) => setSettings(p => ({...p, organizationId: v}))}>
                        <SelectTrigger data-testid="org-select">
                          <SelectValue placeholder="Выберите организацию" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map(o => (
                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Закрыть</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Import Dialog */}
              <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="import-btn">
                    <Download className="w-4 h-4 mr-2" />
                    Импорт ПЗ
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md bg-white">
                  <DialogHeader>
                    <DialogTitle>Загрузить существующее ПЗ</DialogTitle>
                    <DialogDescription>Выберите производственное задание для загрузки</DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="max-h-[400px]">
                    <div className="space-y-2 p-1">
                      {productionTasks.map(task => (
                        <div 
                          key={task.id}
                          className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => loadExistingTask(task.id)}
                          data-testid={`task-item-${task.id}`}
                        >
                          <div className="font-medium">{task.name}</div>
                          <div className="text-xs text-gray-500">{new Date(task.created).toLocaleDateString()}</div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </DialogContent>
              </Dialog>

              {/* PDF Export Button */}
              {selectedPlans.length > 0 && (
                <Button variant="outline" size="sm" onClick={generatePDF} data-testid="pdf-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Сохранить PDF
                </Button>
              )}
            </div>
          </div>

          {/* Task Name Input */}
          <div className="mb-6">
            <Label className="text-sm font-medium mb-2 block">Название ПЗ</Label>
            <Input
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              placeholder="Введите название производственного задания"
              className="max-w-md"
              data-testid="task-name-input"
            />
          </div>

          {/* Tech Cards List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium">Техкарты</Label>
              <Dialog open={planSelectorOpen} onOpenChange={setPlanSelectorOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-plan-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Добавить техкарту
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg bg-white">
                  <DialogHeader>
                    <DialogTitle>Выберите техкарту</DialogTitle>
                    <DialogDescription>Техкарты сгруппированы по папкам</DialogDescription>
                  </DialogHeader>
                  <ScrollArea className="max-h-[500px]">
                    {Object.entries(groupedPlans).map(([folder, plans]) => (
                      <div key={folder} className="mb-4">
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-2">
                          {folder}
                        </div>
                        {plans.map(plan => (
                          <div
                            key={plan.id}
                            className="p-3 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors flex items-center justify-between"
                            onClick={() => addPlanToTask(plan.id)}
                            data-testid={`plan-option-${plan.id}`}
                          >
                            <span className="font-medium">{plan.name}</span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </div>
                        ))}
                      </div>
                    ))}
                  </ScrollArea>
                </DialogContent>
              </Dialog>
            </div>

            {selectedPlans.length === 0 ? (
              <div className="empty-state">
                <ClipboardList className="empty-state-icon" />
                <p className="empty-state-text">Добавьте техкарты для создания ПЗ</p>
              </div>
            ) : (
              <div className="techcard-list" data-testid="techcard-list">
                {selectedPlans.map((plan, index) => (
                  <div key={`${plan.planId}-${index}`} className="techcard-item animate-fade-in" data-testid={`techcard-item-${index}`}>
                    <div>
                      <div className="techcard-name">{plan.planName}</div>
                      <div className="techcard-path">{plan.pathName}</div>
                      {plan.details?.products?.length > 0 && (
                        <div className="techcard-output">
                          Выход: {plan.details.products.map(p => `${p.name} × ${(p.quantity * plan.volume).toFixed(1)}`).join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="techcard-volume flex items-center gap-1">
                      <Label className="text-xs text-gray-500 mr-2">Объем:</Label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-8 h-8 p-0"
                        onClick={() => updatePlanVolume(index, -1)}
                        disabled={plan.volume <= 1}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={plan.volume}
                        onChange={(e) => setPlanVolume(index, e.target.value)}
                        className="w-16 text-center"
                        data-testid={`volume-input-${index}`}
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-8 h-8 p-0"
                        onClick={() => updatePlanVolume(index, 1)}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-sm text-gray-500 tabular-nums">
                      {((plan.details?.totalHours || 0) * plan.volume).toFixed(2)}ч
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removePlanFromTask(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-plan-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Summary Panels */}
          {selectedPlans.length > 0 && (
            <>
              {/* Large Hours Display */}
              <div className="flex items-center justify-center gap-8 mb-6 p-4 bg-slate-50 rounded-xl">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600 tabular-nums">{totalRequiredHours.toFixed(2)}ч</div>
                  <div className="text-sm text-gray-500 mt-1">Требуется часов</div>
                </div>
                <div className="text-3xl text-gray-300">/</div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-green-600 tabular-nums">{totalAvailableHours.toFixed(1)}ч</div>
                  <div className="text-sm text-gray-500 mt-1">Доступно часов</div>
                </div>
              </div>

              {/* Total Output */}
              {totalOutputProducts.length > 0 && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="text-sm font-semibold text-blue-800 mb-2">Суммарный выход продукции:</div>
                  <div className="flex flex-wrap gap-2">
                    {totalOutputProducts.map(prod => (
                      <Badge key={prod.id} variant="secondary" className="bg-white text-blue-700 border border-blue-300">
                        {prod.name} × {prod.totalQuantity.toFixed(1)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="summary-grid">
                {/* Stages Summary */}
                <div className="summary-panel" data-testid="stages-summary">
                  <div className="summary-panel-title">
                    Этапы производства — {totalRequiredHours.toFixed(2)}ч
                  </div>
                  <div className="stage-hours-list">
                    {getAggregatedStages().map(stage => {
                      const hasWorkers = hasAvailableWorkers(stage);
                      return (
                        <div 
                          key={stage.id || stage.name} 
                          className={`stage-hours-item ${!hasWorkers ? 'warning' : ''}`}
                          data-testid={`stage-${stage.name}`}
                        >
                          {!hasWorkers && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                          <span>{stage.name}</span>
                          <span className="stage-hours-value">{stage.totalHours.toFixed(2)}ч</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Materials Summary */}
                <div className="summary-panel" data-testid="materials-summary">
                  <div className="summary-panel-title">
                    Необходимые материалы
                  </div>
                  <div className="material-list">
                    {getAggregatedMaterials().map(mat => {
                      const stockQty = stock[mat.id] || 0;
                      const isLow = stockQty < mat.totalQuantity;
                      return (
                        <div key={mat.id} className="material-item">
                          <span className="material-name">{mat.name}</span>
                          <div className="flex items-center">
                            <span className="material-qty">{mat.totalQuantity.toFixed(2)}</span>
                            <span className={`material-stock ${isLow ? 'low' : ''}`}>
                              (склад: {stockQty.toFixed(2)})
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Create Button */}
          <div className="mt-6 flex justify-end">
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={createTask}
              disabled={loading || !taskName || selectedPlans.length === 0}
              data-testid="create-task-btn"
            >
              {loading ? <span className="loading-spinner mr-2"></span> : <Upload className="w-4 h-4 mr-2" />}
              Создать ПЗ в МойСклад
            </Button>
          </div>
        </section>

        {/* Section 3: Balancing */}
        <section className="section-container" data-testid="balancing-section">
          <div className="section-header">
            <h2 className="section-title">
              <Scale className="w-5 h-5 text-blue-600" />
              Часть 3. Балансировка этапов по сотрудникам
            </h2>
            <div className="flex items-center gap-4">
              {selectedPlans.length > 0 && (
                <div className="flex items-center gap-4 text-sm">
                  {balancing.totalUnderload > 0 && (
                    <span className="text-red-600 font-semibold">
                      Недогрузка: {balancing.totalUnderload.toFixed(2)}ч
                    </span>
                  )}
                  {balancing.totalOverload > 0 && (
                    <span className="text-amber-600 font-semibold">
                      Перегрузка: {balancing.totalOverload.toFixed(2)}ч
                    </span>
                  )}
                  {balancing.totalUnderload === 0 && balancing.totalOverload === 0 && (
                    <span className="text-green-600 font-semibold">
                      Баланс: ✓
                    </span>
                  )}
                </div>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={autoBalance}
                disabled={selectedPlans.length === 0}
                data-testid="auto-balance-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Пересчитать
              </Button>
            </div>
          </div>

          {selectedPlans.length === 0 ? (
            <div className="empty-state">
              <Scale className="empty-state-icon" />
              <p className="empty-state-text">Добавьте техкарты для балансировки</p>
            </div>
          ) : (
            <div className="balancing-grid">
              {/* Assigned */}
              <div className="balancing-panel" data-testid="assigned-panel">
                <div className="summary-panel-title flex items-center justify-between">
                  <span>Распределение по сотрудникам</span>
                  <span className="text-xs font-normal text-gray-400">Перетащите этапы для перераспределения</span>
                </div>
                {Object.entries(balancing.assignments).length === 0 ? (
                  <p className="text-sm text-gray-500">Нет сотрудников с запланированными часами</p>
                ) : (
                  Object.entries(balancing.assignments).map(([empId, data]) => {
                    const remaining = data.totalPlanned - data.allocated;
                    const progressPercent = data.totalPlanned > 0 ? (data.allocated / data.totalPlanned) * 100 : 0;
                    // Inverted: red = underload, green = 100%
                    const progressColor = progressPercent >= 100 ? 'green' : progressPercent >= 80 ? 'yellow' : 'red';
                    
                    return (
                      <div 
                        key={empId} 
                        className="employee-allocation"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, empId)}
                        data-testid={`allocation-${empId}`}
                      >
                        <div className="employee-allocation-header">
                          <span className="employee-allocation-name">{data.name}</span>
                          <span className="employee-allocation-hours">
                            <span className="allocated-hours">{data.allocated.toFixed(2)}ч</span>
                            {" / "}
                            <span>{data.totalPlanned.toFixed(1)}ч</span>
                            {" • "}
                            <span className={`remaining-hours ${remaining < 0 ? 'over' : remaining > 0 ? 'under' : ''}`}>
                              {remaining >= 0 ? `+${remaining.toFixed(2)}ч` : `${remaining.toFixed(2)}ч`}
                            </span>
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div 
                            className={`progress-bar ${progressColor}`}
                            style={{ width: `${Math.min(100, progressPercent)}%` }}
                          ></div>
                        </div>
                        {data.stageInstances.length > 0 && (
                          <div className="allocation-stages mt-2">
                            {data.stageInstances.map((stage) => (
                              <span 
                                key={stage.id} 
                                className="allocation-stage cursor-grab active:cursor-grabbing"
                                draggable
                                onDragStart={(e) => handleDragStart(e, stage, empId)}
                              >
                                <GripVertical className="w-3 h-3 inline mr-1 opacity-50" />
                                {stage.stageName}: {stage.hours.toFixed(2)}ч
                                <span className="text-[10px] opacity-60 ml-1">
                                  ({stage.planName} #{stage.unitIndex + 1})
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Unassigned */}
              <div 
                className="balancing-panel"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, null)}
                data-testid="unassigned-panel"
              >
                <div className="summary-panel-title flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Нераспределенные этапы
                </div>
                {balancing.unassignedStages.length === 0 ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <Check className="w-4 h-4" />
                    Все этапы распределены
                  </div>
                ) : (
                  <div className="unassigned-stages">
                    {balancing.unassignedStages.map((stage) => (
                      <div 
                        key={stage.id} 
                        className="unassigned-stage cursor-grab active:cursor-grabbing"
                        draggable
                        onDragStart={(e) => handleDragStart(e, stage, null)}
                      >
                        <div>
                          <div className="unassigned-stage-name flex items-center gap-1">
                            <GripVertical className="w-3 h-3 opacity-50" />
                            {stage.stageName}
                            <span className="text-[10px] opacity-60">
                              ({stage.planName} #{stage.unitIndex + 1})
                            </span>
                          </div>
                          {stage.suggestedEmployees?.length > 0 && (
                            <div className="suggested-employees">
                              Могут выполнять: {stage.suggestedEmployees.join(", ")}
                            </div>
                          )}
                        </div>
                        <span className="unassigned-stage-hours">{stage.hours.toFixed(2)}ч</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </TooltipProvider>
  );
};

// App Root
function App() {
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    // Check for saved credentials
    const saved = localStorage.getItem("msCredentials");
    if (saved) {
      setCredentials(JSON.parse(saved));
    }
  }, []);

  const handleLogin = (creds) => {
    setCredentials(creds);
    localStorage.setItem("msCredentials", JSON.stringify(creds));
  };

  const handleLogout = () => {
    setCredentials(null);
    localStorage.removeItem("msCredentials");
  };

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      {credentials ? (
        <MainApp credentials={credentials} onLogout={handleLogout} />
      ) : (
        <LoginPage onLogin={handleLogin} />
      )}
    </div>
  );
}

export default App;
